'use client';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useResource } from '@/lib/use-resource';

interface Leaf { name: string; value: string; good: boolean }
interface Pillar { score: number; leaves: Leaf[] }
interface PricePoint { date: string; price: number }
interface Report {
  symbol: string; name: string; sector: string; industry: string;
  price: number; marketCap: string; netDebtEbitda: string;
  q: number; v: number; composite: number; tier: string;
  business: string; businessDetail: string; sectorContext: string;
  pillars: { profitability: Pillar; growth: Pillar; safety: Pillar; payout: Pillar };
  valuation: { relative: Leaf[]; absolute: Leaf[] };
  targets: { fairValue: number; threeYear: number; fiveYear: number; bull: number; asymmetry: string };
  risks: string[]; bottomLine: string;
  priceSeries?: PricePoint[];
}
interface ReportsFile { generatedAt: string | null; priceAsOf?: string; reports: Report[] }

const PILLAR_META: { key: keyof Report['pillars']; label: string; weight: string }[] = [
  { key: 'profitability', label: 'Profitability', weight: '45%' },
  { key: 'growth', label: 'Growth', weight: '20%' },
  { key: 'safety', label: 'Safety', weight: '15%' },
  { key: 'payout', label: 'Shareholder Returns', weight: '20%' },
];

function tierColor(tier: string) {
  if (tier === 'Strong Buy') return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40';
  if (tier === 'Buy') return 'bg-sky-500/15 text-sky-300 border-sky-500/40';
  return 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40';
}
function LeafRow({ l }: { l: Leaf }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px] py-0.5">
      <span className="text-[#94a3b8] truncate">{l.name}</span>
      <span className={`tabular-nums font-medium ${l.good ? 'text-emerald-300' : 'text-rose-300'}`}>{l.value}</span>
    </div>
  );
}
const Section = ({ title }: { title: string }) =>
  <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1.5">{title}</div>;

function PriceChart({ series }: { series: PricePoint[] }) {
  if (!series || series.length < 2) return <div className="text-[11px] text-[#64748b] py-6 text-center">price history unavailable</div>;
  const first = series[0].price, last = series[series.length - 1].price;
  const up = last >= first;
  const col = up ? '#34d399' : '#fb7185';
  return (
    <ResponsiveContainer width="100%" height={150}>
      <AreaChart data={series} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="azr-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={col} stopOpacity={0.35} />
            <stop offset="100%" stopColor={col} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" tickFormatter={(d) => String(d).slice(2, 7)} tick={{ fontSize: 9, fill: '#64748b' }} minTickGap={28} axisLine={false} tickLine={false} />
        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 9, fill: '#64748b' }} width={44} axisLine={false} tickLine={false} tickFormatter={(v) => '$' + Number(v).toLocaleString()} />
        <Tooltip contentStyle={{ background: '#0a0e14', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} labelStyle={{ color: '#94a3b8' }} formatter={(v) => ['$' + Number(v).toLocaleString(), 'Price'] as [string, string]} />
        <Area type="monotone" dataKey="price" stroke={col} strokeWidth={2} fill="url(#azr-fill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function Modal({ r, onClose }: { r: Report; onClose: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const upside = Math.round((r.targets.fairValue / r.price - 1) * 100);
  const t3Up = Math.round((r.targets.threeYear / r.price - 1) * 100);
  return createPortal(
    <div className="fixed inset-0 z-[2147483000] flex items-start justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="my-6 w-full max-w-3xl rounded-2xl border border-[#1e293b] bg-[#0d1117] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-3 border-b border-[#1e293b] bg-[#0d1117] rounded-t-2xl">
          <span className="text-[18px] font-bold text-white">{r.symbol}</span>
          <span className="flex-1 min-w-0"><span className="text-[13px] text-[#e2e8f0]">{r.name}</span><span className="text-[11px] text-[#64748b] ml-2">{r.industry}</span></span>
          <span className="text-[11px] text-[#94a3b8] tabular-nums hidden sm:block">${r.price.toLocaleString()} · {r.marketCap}</span>
          <span className="text-[11px] text-[#94a3b8] tabular-nums">Q {r.q.toFixed(2)} · V {r.v.toFixed(2)}</span>
          <span className="text-[12px] font-bold text-white tabular-nums">C {r.composite.toFixed(2)}</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${tierColor(r.tier)}`}>{r.tier}</span>
          <button onClick={onClose} className="text-[#64748b] hover:text-white text-lg leading-none px-1">×</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[12px] rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-2.5">
            <span className="text-[10px] uppercase tracking-wider text-amber-300/80 w-full sm:w-auto">Target</span>
            <span className="text-[#e2e8f0]">Fair value <b className="text-amber-300 tabular-nums text-[14px]">${r.targets.fairValue.toLocaleString()}</b> <span className={`font-semibold ${upside >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>({upside >= 0 ? '+' : ''}{upside}%)</span></span>
            <span className="text-[#e2e8f0]">3-yr target <b className="text-amber-300 tabular-nums text-[14px]">${r.targets.threeYear.toLocaleString()}</b> <span className={`font-semibold ${t3Up >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>({t3Up >= 0 ? '+' : ''}{t3Up}% to target)</span></span>
            <span className="text-[#94a3b8]">5-yr ${r.targets.fiveYear.toLocaleString()}</span>
            <span className="text-[#94a3b8]">Bull ${r.targets.bull.toLocaleString()}</span>
            <span className="text-[#94a3b8]">Asymmetry {r.targets.asymmetry}</span>
            <span className="text-[#94a3b8]">Net debt/EBITDA {r.netDebtEbitda}</span>
          </div>
          <PriceChart series={r.priceSeries ?? []} />

          <div><Section title="Business" /><p className="text-[12.5px] leading-relaxed text-[#cbd5e1]">{r.businessDetail}</p></div>
          <div><Section title="Sector" /><p className="text-[12.5px] leading-relaxed text-[#cbd5e1]">{r.sectorContext}</p></div>

          <div>
            <Section title={`Quality ${r.q.toFixed(2)} — four pillars`} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {PILLAR_META.map(pm => {
                const p = r.pillars[pm.key];
                return (
                  <div key={pm.key} className="rounded-lg border border-[#1e293b] bg-[#0a0e14] p-2.5">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-[11px] font-semibold text-[#e2e8f0]">{pm.label}</span>
                      <span className="text-[9px] text-[#64748b]">{pm.weight}</span>
                    </div>
                    <div className={`text-[13px] font-bold tabular-nums mb-1.5 ${p.score >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{p.score >= 0 ? '+' : ''}{p.score.toFixed(2)}</div>
                    {p.leaves.map((l, i) => <LeafRow key={i} l={l} />)}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-lg border border-[#1e293b] bg-[#0a0e14] p-2.5"><Section title={`Valuation ${r.v.toFixed(2)} — relative (peers)`} />{r.valuation.relative.map((l, i) => <LeafRow key={i} l={l} />)}</div>
            <div className="rounded-lg border border-[#1e293b] bg-[#0a0e14] p-2.5"><Section title="Valuation — absolute (lenses)" />{r.valuation.absolute.map((l, i) => <LeafRow key={i} l={l} />)}</div>
          </div>

          <div><Section title="What to watch" /><ul className="list-disc list-inside text-[11.5px] text-[#cbd5e1] space-y-0.5">{r.risks.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          <p className="text-[12px] text-[#e2e8f0] border-l-2 border-amber-500/50 pl-3"><span className="font-semibold text-amber-300">Bottom line. </span>{r.bottomLine}</p>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function AlphaZoneReports() {
  const { data } = useResource<ReportsFile>('/api/alpha-zone-reports', 60_000);
  const [sel, setSel] = useState<string | null>(null);
  const reports = data?.reports ?? [];
  const active = reports.find(r => r.symbol === sel) ?? null;
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-[18px] font-bold text-white tracking-tight">Alpha Zone Reports</h2>
        <p className="text-[11px] text-[#64748b]">Click any name for a full research note — chart, business, sector, composite breakdown, factor leaves, valuation, and target. {data?.priceAsOf ? `Prices ${data.priceAsOf}.` : ''}</p>
      </div>
      {reports.length === 0
        ? <div className="text-[#94a3b8] text-sm py-4">Loading reports…</div>
        : reports.map(r => {
            const upside = Math.round((r.targets.fairValue / r.price - 1) * 100);
            return (
              <button key={r.symbol} onClick={() => setSel(r.symbol)} className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl border border-[#1e293b] bg-[#0d1117] hover:border-[#334155] hover:bg-white/[0.02] transition-colors">
                <span className="text-[15px] font-bold text-white w-16">{r.symbol}</span>
                <span className="flex-1 min-w-0"><span className="text-[13px] text-[#e2e8f0]">{r.name}</span><span className="text-[11px] text-[#64748b] ml-2">{r.sector}</span></span>
                <span className="text-[11px] text-[#94a3b8] tabular-nums hidden md:block">${r.price.toLocaleString()} · {r.marketCap}</span>
                <span className="text-[11px] text-emerald-300/90 tabular-nums hidden sm:block">{upside >= 0 ? '+' : ''}{upside}% to FV</span>
                <span className="text-[11px] text-[#94a3b8] tabular-nums">Q {r.q.toFixed(2)} · V {r.v.toFixed(2)}</span>
                <span className="text-[12px] font-bold text-white tabular-nums">C {r.composite.toFixed(2)}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${tierColor(r.tier)}`}>{r.tier}</span>
              </button>
            );
          })}
      {active && <Modal r={active} onClose={() => setSel(null)} />}
    </div>
  );
}
